import { useState, useContext, useEffect } from 'react';
import { Modal, Button, Tab } from 'semantic-ui-react';
import TrainingContext from './hooks/TrainingContext';
import UsersViewedTab from './UsersViewedTab';
import UsersAssignTab from './UsersAssignTab';
import QuizConsolidatedResultsModal from './QuizConsolidatedResultsModal';

const UsersViewedModal = ({ open, onClose, entry, interactions = [], onEntryUpdated }) => {
  const context = useContext(TrainingContext);
  const allUsers = context?.users || [];
  const loading = context?.loadingUsers || false;
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [consolidatedResultsOpen, setConsolidatedResultsOpen] = useState(false);

  // Initialize selected users from entry.assigned_to
  useEffect(() => {
    if (entry && open) {
      const assignedTo = entry.assigned_to || [];
      const assignedToArray = Array.isArray(assignedTo) ? assignedTo : [];
      const hasAll = assignedToArray.includes('all');
      setSelectAll(hasAll);
      if (hasAll) {
        setSelectedUsers(['all']);
      } else {
        setSelectedUsers(assignedToArray.filter((id) => id !== 'all'));
      }
    }
  }, [entry, open]);

  if (!entry) return null;

  // Filter only staff users
  const users = allUsers.filter((user) => {
    const status = user.status || [];
    return Array.isArray(status) && status.includes('staff');
  });

  const readedBy = entry.readed_by || [];
  const readedByArray = Array.isArray(readedBy) ? readedBy : [];

  // Separate users into read and unread
  const usersWhoRead = users.filter((user) => readedByArray.includes(user.id));
  const usersWhoHaventRead = users.filter((user) => !readedByArray.includes(user.id));

  // Check if entry is a quiz
  const isQuiz = entry?.type === 'quiz';

  // Get quiz interaction for a specific user
  const getQuizInteraction = (userId) => {
    return interactions.find(
      (interaction) =>
        interaction.content?.entryId === entry.id &&
        interaction.type === 'quiz' &&
        interaction.user_id === userId
    );
  };

  // Tab 1: Users who have seen the content
  const viewedTab = (
    <Tab.Pane>
      <UsersViewedTab
        users={users}
        usersWhoRead={usersWhoRead}
        usersWhoHaventRead={usersWhoHaventRead}
        loading={loading}
        isQuiz={isQuiz}
        getQuizInteraction={getQuizInteraction}
        entry={entry}
      />
    </Tab.Pane>
  );

  // Tab 2: Assign users to content
  const assignTab = (
    <Tab.Pane>
      <UsersAssignTab
        users={users}
        loading={loading}
        entry={entry}
        selectedUsers={selectedUsers}
        selectAll={selectAll}
        onEntryUpdated={onEntryUpdated}
        context={context}
      />
    </Tab.Pane>
  );

  const panes = [
    {
      menuItem: { key: 'viewed', icon: 'eye', content: 'Usuarios que han visto' },
      render: () => viewedTab,
    },
    {
      menuItem: { key: 'assign', icon: 'user plus', content: 'Asignar usuarios' },
      render: () => assignTab,
    },
  ];

  return (
    <>
      <Modal open={open} onClose={onClose} size="large">
        <Modal.Header>Gestión de Usuarios de la asignación: {entry.title}</Modal.Header>
        <Modal.Content scrolling>
          {isQuiz && (
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                primary
                icon="chart bar"
                content="Ver Resultados Consolidados"
                onClick={() => setConsolidatedResultsOpen(true)}
              />
            </div>
          )}
          <Tab
            panes={panes}
            activeIndex={activeTab}
            onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onClose}>Cerrar</Button>
        </Modal.Actions>
      </Modal>

      <QuizConsolidatedResultsModal
        open={consolidatedResultsOpen}
        onClose={() => setConsolidatedResultsOpen(false)}
        entry={entry}
        interactions={interactions}
        users={users}
      />
    </>
  );
};

export default UsersViewedModal;
